import { DialogStep } from "../stores/useDialogs";
import { MapNPCUnlockMode, type MapData, type NPC } from "../types/MapData.types";
import type { WorldConfig, WorldMapConfig } from "../types/WorldCfg.types";
import type { GameProgress } from "../types/UserDto";

type GameData = { worldConfig: WorldConfig; mapsData: { [mapId: string]: MapData } };

let cachedGameData: GameData | null = null;
let gameDataPromise: Promise<GameData> | null = null;

// Helper functions for working with GameProgress array
export const findNPCProgress = (progress: GameProgress[], mapId: string, npcId: string): GameProgress | null => {
  return progress.find(item => item.mapId === mapId && item.npcId === npcId) || null;
};

export const updateNPCProgress = (progress: GameProgress[], mapId: string, npcId: string, updates: Partial<GameProgress>): GameProgress[] => {
  return progress.map(item =>
    item.mapId === mapId && item.npcId === npcId
      ? { ...item, ...updates }
      : item
  );
};

export const addNPCProgress = (progress: GameProgress[], mapId: string, npcId: string, npcProgress: Omit<GameProgress, 'mapId' | 'npcId'>): GameProgress[] => {
  const existingIndex = progress.findIndex(item => item.mapId === mapId && item.npcId === npcId);

  if (existingIndex >= 0) {
    // Update existing
    return progress.map((item, index) =>
      index === existingIndex
        ? { mapId, npcId, ...npcProgress }
        : item
    );
  } else {
    // Add new
    return [...progress, { mapId, npcId, ...npcProgress }];
  }
};

export const loadGameData = async (): Promise<GameData> => {
  if (cachedGameData) {
    return cachedGameData;
  }

  if (!gameDataPromise) {
    gameDataPromise = (async () => {
      // Load worldConfig
      const worldResponse = await fetch("/data/worldConfig.json");
      const worldConfig = await worldResponse.json() as WorldConfig;

      // Load all maps in parallel
      const mapPromises = worldConfig.maps.map(async (map: WorldMapConfig) => {
        const response = await fetch(`/data/maps/map${map.id}.json`);
        const mapsData = await response.json() as MapData;
        return { [map.id]: mapsData } as { [mapId: string]: MapData };
      });

      const mapResults = await Promise.all(mapPromises);
      const mapsData = mapResults.reduce((acc, curr) => ({ ...acc, ...curr }), {} as { [mapId: string]: MapData });

      return { worldConfig, mapsData };
    })()
      .then((result) => {
        cachedGameData = result;
        return result;
      })
      .finally(() => {
        gameDataPromise = null;
      });
  }

  return gameDataPromise;
};

export const clearGameDataCache = (): void => {
  cachedGameData = null;
  gameDataPromise = null;
};

export const createInitialProgress = (gameData: { worldConfig: WorldConfig; mapsData: { [mapId: string]: MapData } }, unlockedMaps: Set<string>, allNpcIndependent: boolean = false): GameProgress[] => {
  const progress: GameProgress[] = [];

  // Default progress object template
  const defaultProgress = {
    isCompleted: false,
    dialogStep: DialogStep.FACT,
    factIndex: 0,
    sectionIndex: 0,
  };

  for (const [mapId, mapInfo] of Object.entries(gameData.mapsData)) {
    const isMapUnlocked = unlockedMaps.has(mapId);

    for (const npc of mapInfo.npcs) {
      let isNPCUnlocked = false;

      if (isMapUnlocked) {
        if (allNpcIndependent || mapInfo.npcUnlockMode === MapNPCUnlockMode.INDEPENDENT) {
          // Independent mode (global or per-map): all NPCs are unlocked when map is unlocked
          isNPCUnlocked = true;
        } else {
          // Sequential mode: only first NPC (prerequisiteNpcId === null) is unlocked
          isNPCUnlocked = npc.prerequisiteNpcId === null;
        }
      }

      progress.push({
        mapId,
        npcId: npc.id,
        isUnlocked: isNPCUnlocked,
        ...defaultProgress,
      });
    }
  }

  return progress;
};

export const syncProgressWithMapConfigs = (
  existingProgress: GameProgress[],
  gameData: { worldConfig: WorldConfig; mapsData: { [mapId: string]: MapData } },
  unlockedMaps: Set<string>,
  allNpcIndependent: boolean = false
): GameProgress[] => {
  let updatedProgress: GameProgress[] = [...existingProgress];

  // Default progress object template for new NPCs
  const defaultProgress = {
    isUnlocked: false,
    isCompleted: false,
    dialogStep: DialogStep.FACT,
    factIndex: 0,
    sectionIndex: 0,
  };

  for (const [mapId, mapInfo] of Object.entries(gameData.mapsData)) {
    const isMapUnlocked = unlockedMaps.has(mapId);

    for (const npc of mapInfo.npcs) {
      // Find or create NPC progress
      let npcProgress = findNPCProgress(updatedProgress, mapId, npc.id);

      if (!npcProgress) {
        // Initialize NPC progress if it doesn't exist
        updatedProgress = addNPCProgress(updatedProgress, mapId, npc.id, defaultProgress);
        npcProgress = findNPCProgress(updatedProgress, mapId, npc.id)!;
      }

      // Skip if map is not unlocked or NPC is already completed
      if (!isMapUnlocked || npcProgress.isCompleted) continue;

      // Determine if NPC should be unlocked
      let shouldUnlock = false;

      if (allNpcIndependent || mapInfo.npcUnlockMode === MapNPCUnlockMode.INDEPENDENT) {
        // Independent mode: unlock all NPCs on unlocked maps
        shouldUnlock = true;
      } else if (npc.prerequisiteNpcId === null) {
        // Sequential mode: first NPC is always unlocked
        shouldUnlock = true;
      } else {
        // Sequential mode: check if prerequisite NPC is completed
        const prerequisiteNPC = findNPCProgress(updatedProgress, mapId, npc.prerequisiteNpcId);
        shouldUnlock = prerequisiteNPC?.isCompleted || false;
      }

      if (shouldUnlock) {
        updatedProgress = updateNPCProgress(updatedProgress, mapId, npc.id, { isUnlocked: true });
      }
    }
  }

  return updatedProgress;
};

export const unlockNextNPC = (progress: GameProgress[], mapId: string, completedNpcId: string, mapData: MapData): GameProgress[] => {
  const nextNPC = mapData.npcs.find((npc: NPC) => npc.prerequisiteNpcId === completedNpcId);

  if (nextNPC && findNPCProgress(progress, mapId, nextNPC.id)) {
    return updateNPCProgress(progress, mapId, nextNPC.id, { isUnlocked: true });
  }

  return progress;
};

export const checkMapUnlock = (
  progress: GameProgress[],
  unlockedMaps: Set<string>,
  gameData: { worldConfig: WorldConfig; mapsData: { [mapId: string]: MapData } },
  completedMapId: string,
  completedNpcId: string
): { progress: GameProgress[]; unlockedMaps: Set<string> } => {
  let newProgress = [...progress];
  let newUnlockedMaps = new Set(unlockedMaps);

  // Find the completed NPC and check if it unlocks any map
  const mapData = gameData.mapsData[completedMapId];
  const completedNPC = mapData?.npcs.find(npc => npc.id === completedNpcId);

  // If NPC doesn't unlock a map or map is already unlocked, return current state
  if (!completedNPC?.unlocksMapId || newUnlockedMaps.has(completedNPC.unlocksMapId)) {
    return { progress: newProgress, unlockedMaps: newUnlockedMaps };
  }

  // Add the newly unlocked map
  const targetMapId = completedNPC.unlocksMapId;
  newUnlockedMaps.add(targetMapId);

  // Get data for the newly unlocked map
  const targetMapData = gameData.mapsData[targetMapId];
  if (!targetMapData) return { progress: newProgress, unlockedMaps: newUnlockedMaps };

  // Unlock NPCs on the newly unlocked map based on its unlock mode
  if (targetMapData.npcUnlockMode === MapNPCUnlockMode.INDEPENDENT) {
    // Independent mode: unlock all NPCs at once
    targetMapData.npcs.forEach((npc: NPC) => {
      const npcProgress = findNPCProgress(newProgress, targetMapId, npc.id);
      if (npcProgress) {
        newProgress = updateNPCProgress(newProgress, targetMapId, npc.id, { isUnlocked: true });
      }
    });
  } else {
    // Sequential mode: unlock only first NPC
    const firstNPC = targetMapData.npcs.find((npc: NPC) => npc.prerequisiteNpcId === null);
    if (firstNPC && findNPCProgress(newProgress, targetMapId, firstNPC.id)) {
      newProgress = updateNPCProgress(newProgress, targetMapId, firstNPC.id, { isUnlocked: true });
    }
  }

  return { progress: newProgress, unlockedMaps: newUnlockedMaps };
};

// Add all maps that have no NPC dependencies (no NPC unlocks them) to unlockedMaps
export const addDependencyFreeMaps = (unlockedMaps: Set<string>, gameData: { worldConfig: WorldConfig; mapsData: { [mapId: string]: MapData } }): Set<string> => {
  try {
    const augmented = new Set(unlockedMaps);

    // Find all maps that are unlocked by NPCs
    const mapsWithDependencies = new Set<string>();
    for (const map of Object.values(gameData.mapsData)) {
      for (const npc of map.npcs) {
        if (npc.unlocksMapId) mapsWithDependencies.add(npc.unlocksMapId);
      }
    }

    // Add maps without dependencies (except map "1" which is always unlocked)
    for (const mapId of Object.keys(gameData.mapsData)) {
      if (mapId !== "1" && !mapsWithDependencies.has(mapId)) {
        augmented.add(mapId);
      }
    }

    return augmented;
  } catch (e) {
    console.warn("⚠️ Failed to auto-unlock dependency-free maps:", e);
    return unlockedMaps;
  }
};


